import React, { useEffect, useState } from 'react';
import { BarChart, PieChart, LineChart, TrendingUp, Users, Mail, MessageSquare, Clock, Calendar, CheckCircle, AlertCircle, RefreshCw, ChevronRight, Download, Share2, Loader } from 'lucide-react';
interface CampaignDashboardProps {
  campaignName: string;
  startDate: Date;
  metrics: {
    sent: number;
    delivered: number;
    opened: number;
    replied: number;
    connected: number;
    total: number;
  };
  dailyStats: {
    date: string;
    sent: number;
    replies: number;
  }[];
}
export const CampaignDashboard = ({
  campaignName,
  startDate,
  metrics,
  dailyStats
}: CampaignDashboardProps) => {
  // State for animation and loading
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [animatedMetrics, setAnimatedMetrics] = useState({
    sent: 0,
    delivered: 0,
    opened: 0,
    replied: 0,
    connected: 0
  });
  // Calculate percentages for progress bars
  const deliveryRate = Math.round(metrics.delivered / metrics.sent * 100) || 0;
  const openRate = Math.round(metrics.opened / metrics.delivered * 100) || 0;
  const replyRate = Math.round(metrics.replied / metrics.opened * 100) || 0;
  const connectionRate = Math.round(metrics.connected / metrics.total * 100) || 0;
  // Format date
  const formattedDate = startDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  // Find the day with the highest reply count
  const bestDay = [...dailyStats].sort((a, b) => b.replies - a.replies)[0];
  // Effect to handle the loading and animation sequence
  useEffect(() => {
    // First make the component visible with opacity 0
    setIsVisible(true);
    // After a short delay, start loading animation
    const loadingTimer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    // Animate metrics counting up
    const metricsTimer = setTimeout(() => {
      const duration = 1500; // ms
      const steps = 20;
      const interval = duration / steps;
      let step = 0;
      const animationInterval = setInterval(() => {
        step++;
        const progress = step / steps;
        setAnimatedMetrics({
          sent: Math.round(metrics.sent * progress),
          delivered: Math.round(metrics.delivered * progress),
          opened: Math.round(metrics.opened * progress),
          replied: Math.round(metrics.replied * progress),
          connected: Math.round(metrics.connected * progress)
        });
        if (step >= steps) {
          clearInterval(animationInterval);
          setAnimatedMetrics(metrics); // Ensure final values are exact
        }
      }, interval);
    }, 1800);
    return () => {
      clearTimeout(loadingTimer);
      clearTimeout(metricsTimer);
    };
  }, [metrics]);
  // If not visible yet, render nothing
  if (!isVisible) return null;
  return <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 transition-all duration-500 ${isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'}`} data-id="element-172">
      {isLoading ? <div className="flex flex-col items-center justify-center py-12" data-id="element-173">
          <Loader className="h-12 w-12 text-[#6366f1] animate-spin mb-4" data-id="element-174" />
          <h3 className="text-lg font-medium text-gray-700" data-id="element-175">
            Preparing your campaign dashboard...
          </h3>
          <p className="text-sm text-gray-500 mt-2" data-id="element-176">
            Analyzing data and generating insights
          </p>
        </div> : <>
          <div className="flex justify-between items-center mb-6" data-id="element-177">
            <div data-id="element-178">
              <h2 className="text-xl font-semibold text-gray-800 animate-fade-in" data-id="element-179">
                {campaignName} Dashboard
              </h2>
              <p className="text-sm text-gray-500" data-id="element-180">
                Started on {formattedDate} • Running for{' '}
                {Math.floor(Math.random() * 5) + 1} days
              </p>
            </div>
            <div className="flex gap-2" data-id="element-181">
              <button className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors" data-id="element-182">
                <RefreshCw size={14} data-id="element-183" />
                Refresh
              </button>
              <button className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors" data-id="element-184">
                <Download size={14} data-id="element-185" />
                Export
              </button>
              <button className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors" data-id="element-186">
                <Share2 size={14} data-id="element-187" />
                Share
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6" data-id="element-188">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 transform transition-all duration-300 hover:shadow-md animate-fade-in" data-id="element-189">
              <div className="flex justify-between items-start" data-id="element-190">
                <div data-id="element-191">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-192">Messages Sent</p>
                  <p className="text-2xl font-semibold" data-id="element-193">
                    {animatedMetrics.sent}
                  </p>
                </div>
                <div className="bg-blue-100 p-2 rounded-md" data-id="element-194">
                  <Mail className="h-5 w-5 text-blue-600" data-id="element-195" />
                </div>
              </div>
              <div className="mt-2 text-xs text-green-600 flex items-center" data-id="element-196">
                <TrendingUp size={12} className="mr-1" data-id="element-197" />
                {Math.floor(Math.random() * 10) + 5}% from yesterday
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 transform transition-all duration-300 hover:shadow-md animate-fade-in delay-100" data-id="element-198">
              <div className="flex justify-between items-start" data-id="element-199">
                <div data-id="element-200">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-201">Open Rate</p>
                  <p className="text-2xl font-semibold" data-id="element-202">{openRate}%</p>
                </div>
                <div className="bg-green-100 p-2 rounded-md" data-id="element-203">
                  <CheckCircle className="h-5 w-5 text-green-600" data-id="element-204" />
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500" data-id="element-205">
                Industry average: 21%
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 transform transition-all duration-300 hover:shadow-md animate-fade-in delay-200" data-id="element-206">
              <div className="flex justify-between items-start" data-id="element-207">
                <div data-id="element-208">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-209">Reply Rate</p>
                  <p className="text-2xl font-semibold" data-id="element-210">{replyRate}%</p>
                </div>
                <div className="bg-purple-100 p-2 rounded-md" data-id="element-211">
                  <MessageSquare className="h-5 w-5 text-purple-600" data-id="element-212" />
                </div>
              </div>
              <div className="mt-2 text-xs text-green-600 flex items-center" data-id="element-213">
                <TrendingUp size={12} className="mr-1" data-id="element-214" />
                {Math.floor(Math.random() * 8) + 2}% above target
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 transform transition-all duration-300 hover:shadow-md animate-fade-in delay-300" data-id="element-215">
              <div className="flex justify-between items-start" data-id="element-216">
                <div data-id="element-217">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-218">Connections</p>
                  <p className="text-2xl font-semibold" data-id="element-219">
                    {animatedMetrics.connected}
                  </p>
                </div>
                <div className="bg-yellow-100 p-2 rounded-md" data-id="element-220">
                  <Users className="h-5 w-5 text-yellow-600" data-id="element-221" />
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500" data-id="element-222">
                {connectionRate}% connection rate
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-2 gap-6 mb-6" data-id="element-223">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 transform transition-all duration-300 hover:shadow-md animate-fade-in delay-400" data-id="element-224">
              <h3 className="text-sm font-medium text-gray-700 mb-4" data-id="element-225">
                Message Funnel
              </h3>
              <div className="space-y-4" data-id="element-226">
                <div data-id="element-227">
                  <div className="flex justify-between text-xs text-gray-600 mb-1" data-id="element-228">
                    <span data-id="element-229">Sent</span>
                    <span data-id="element-230">{animatedMetrics.sent} messages</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-231">
                    <div className="bg-blue-500 h-2 rounded-full transition-all duration-1000" style={{
                  width: '100%'
                }} data-id="element-232"></div>
                  </div>
                </div>
                <div data-id="element-233">
                  <div className="flex justify-between text-xs text-gray-600 mb-1" data-id="element-234">
                    <span data-id="element-235">Delivered</span>
                    <span data-id="element-236">
                      {animatedMetrics.delivered} messages ({deliveryRate}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-237">
                    <div className="bg-blue-500 h-2 rounded-full transition-all duration-1000" style={{
                  width: `${deliveryRate}%`
                }} data-id="element-238"></div>
                  </div>
                </div>
                <div data-id="element-239">
                  <div className="flex justify-between text-xs text-gray-600 mb-1" data-id="element-240">
                    <span data-id="element-241">Opened</span>
                    <span data-id="element-242">
                      {animatedMetrics.opened} messages ({openRate}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-243">
                    <div className="bg-green-500 h-2 rounded-full transition-all duration-1000" style={{
                  width: `${openRate}%`
                }} data-id="element-244"></div>
                  </div>
                </div>
                <div data-id="element-245">
                  <div className="flex justify-between text-xs text-gray-600 mb-1" data-id="element-246">
                    <span data-id="element-247">Replied</span>
                    <span data-id="element-248">
                      {animatedMetrics.replied} messages ({replyRate}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-249">
                    <div className="bg-purple-500 h-2 rounded-full transition-all duration-1000" style={{
                  width: `${replyRate}%`
                }} data-id="element-250"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 transform transition-all duration-300 hover:shadow-md animate-fade-in delay-500" data-id="element-251">
              <h3 className="text-sm font-medium text-gray-700 mb-4" data-id="element-252">
                Daily Activity
              </h3>
              <div className="h-[180px] flex items-end justify-between px-2" data-id="element-253">
                {dailyStats.map((day, index) => <div key={index} className="flex flex-col items-center" data-id="element-254">
                    <div className="flex flex-col items-center gap-1" data-id="element-255">
                      <div className="bg-blue-500 w-8 rounded-t-sm transition-all duration-1000" style={{
                  height: `${day.sent / 20 * 100}px`
                }} data-id="element-256"></div>
                      <div className="bg-purple-500 w-8 rounded-t-sm transition-all duration-1000" style={{
                  height: `${day.replies / 5 * 100}px`
                }} data-id="element-257"></div>
                    </div>
                    <span className="text-xs text-gray-500 mt-2" data-id="element-258">
                      {day.date}
                    </span>
                  </div>)}
              </div>
              <div className="flex justify-center mt-4" data-id="element-259">
                <div className="flex items-center gap-4" data-id="element-260">
                  <div className="flex items-center gap-1" data-id="element-261">
                    <div className="w-3 h-3 bg-blue-500 rounded-sm" data-id="element-262"></div>
                    <span className="text-xs text-gray-600" data-id="element-263">Sent</span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-264">
                    <div className="w-3 h-3 bg-purple-500 rounded-sm" data-id="element-265"></div>
                    <span className="text-xs text-gray-600" data-id="element-266">Replies</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Insights Section */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 mb-6 transform transition-all duration-300 hover:shadow-md animate-fade-in delay-600" data-id="element-267">
            <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-268">
              Campaign Insights
            </h3>
            <div className="grid grid-cols-3 gap-4" data-id="element-269">
              <div className="bg-white p-3 rounded-md border border-gray-100 transition-all duration-300 hover:shadow-sm" data-id="element-270">
                <div className="flex items-center gap-2 mb-2" data-id="element-271">
                  <Calendar size={16} className="text-blue-600" data-id="element-272" />
                  <span className="text-sm font-medium" data-id="element-273">
                    Best Performing Day
                  </span>
                </div>
                <p className="text-sm text-gray-600" data-id="element-274">
                  {bestDay?.date} with {bestDay?.replies} replies
                </p>
              </div>
              <div className="bg-white p-3 rounded-md border border-gray-100 transition-all duration-300 hover:shadow-sm" data-id="element-275">
                <div className="flex items-center gap-2 mb-2" data-id="element-276">
                  <Clock size={16} className="text-green-600" data-id="element-277" />
                  <span className="text-sm font-medium" data-id="element-278">Best Time to Send</span>
                </div>
                <p className="text-sm text-gray-600" data-id="element-279">
                  10:00 AM - 11:00 AM (42% open rate)
                </p>
              </div>
              <div className="bg-white p-3 rounded-md border border-gray-100 transition-all duration-300 hover:shadow-sm" data-id="element-280">
                <div className="flex items-center gap-2 mb-2" data-id="element-281">
                  <AlertCircle size={16} className="text-yellow-600" data-id="element-282" />
                  <span className="text-sm font-medium" data-id="element-283">
                    Improvement Opportunity
                  </span>
                </div>
                <p className="text-sm text-gray-600" data-id="element-284">
                  Increase follow-up messages to boost response rate
                </p>
              </div>
            </div>
          </div>

          {/* Top Prospects Section */}
          <div className="animate-fade-in delay-700" data-id="element-285">
            <div className="flex justify-between items-center mb-3" data-id="element-286">
              <h3 className="text-sm font-medium text-gray-700" data-id="element-287">
                Top Engaged Prospects
              </h3>
              <button className="text-xs text-[#6366f1] flex items-center transition-colors hover:text-[#5457e5]" data-id="element-288">
                View all <ChevronRight size={14} data-id="element-289" />
              </button>
            </div>
            <div className="bg-gray-50 rounded-lg border border-gray-100 overflow-hidden transform transition-all duration-300 hover:shadow-md" data-id="element-290">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-291">
                <thead className="bg-gray-50" data-id="element-292">
                  <tr data-id="element-293">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-294">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-295">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-296">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-297">
                      Engagement
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-298">
                  <tr className="transition-colors hover:bg-gray-50" data-id="element-299">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-300">
                      <div className="flex items-center" data-id="element-301">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600" data-id="element-302">
                          S
                        </div>
                        <div className="ml-3" data-id="element-303">
                          <div className="text-sm font-medium text-gray-900" data-id="element-304">
                            Sarah Williams
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-305">CTO</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-306">
                      Insight Analytics
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-307">
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800" data-id="element-308">
                        Replied
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-309">
                      High (4 messages)
                    </td>
                  </tr>
                  <tr className="transition-colors hover:bg-gray-50" data-id="element-310">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-311">
                      <div className="flex items-center" data-id="element-312">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600" data-id="element-313">
                          M
                        </div>
                        <div className="ml-3" data-id="element-314">
                          <div className="text-sm font-medium text-gray-900" data-id="element-315">
                            Michael Brown
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-316">CEO</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-317">
                      Digital Ventures
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-318">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800" data-id="element-319">
                        Opened
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-320">
                      Medium (2 messages)
                    </td>
                  </tr>
                  <tr className="transition-colors hover:bg-gray-50" data-id="element-321">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-322">
                      <div className="flex items-center" data-id="element-323">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600" data-id="element-324">
                          E
                        </div>
                        <div className="ml-3" data-id="element-325">
                          <div className="text-sm font-medium text-gray-900" data-id="element-326">
                            Emily Davis
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-327">CFO</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-328">
                      Smart Systems
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-329">
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800" data-id="element-330">
                        Replied
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-331">
                      High (5 messages)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>}
    </div>;
};
// Add these animations to your CSS
const cssToAdd = `
@keyframes fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in {
  animation: fade-in 0.5s ease-out forwards;
}

.delay-100 {
  animation-delay: 100ms;
}

.delay-200 {
  animation-delay: 200ms;
}

.delay-300 {
  animation-delay: 300ms;
}

.delay-400 {
  animation-delay: 400ms;
}

.delay-500 {
  animation-delay: 500ms;
}

.delay-600 {
  animation-delay: 600ms;
}

.delay-700 {
  animation-delay: 700ms;
}
`;